/**
 * Utilitários comuns aos itens de MARCAÇÃO NO PLANO (N3): malha de coordenadas,
 * escrita de pontos, classificação de quadriláteros e as marcas de revisão.
 *
 * Moram aqui para que três itens diferentes — pontos na figura, vértices do quadrado,
 * simetria na malha — escrevam coordenadas do mesmo jeito, desenhem a malha do mesmo
 * jeito e marquem acerto, sobra e falta com os mesmos anéis.
 */

import type { GeoId, GeoObject, GPoint } from '../../geom/construction.ts';
import type { Vec2 } from '../../geom/vec.ts';
import type { Shape } from '../../render/scene.ts';
import { txt, type RichText } from '../../schema/richtext.ts';

/** Inteiro com o sinal de menos tipográfico: "−3", e não o hífen "-3". */
export const inteiroTxt = (n: number): string => {
  const r = Math.round(n);
  return r < 0 ? `−${Math.abs(r)}` : String(r);
};

export const pontoTxt = (p: Vec2): string => `(${inteiroTxt(p.x)}, ${inteiroTxt(p.y)})`;

/** Pontos separados por ponto e vírgula: a vírgula já separa as coordenadas. */
export const listaTxt = (ps: readonly Vec2[]): string => ps.map(pontoTxt).join('; ');

export const mesmoPonto = (a: Vec2, b: Vec2, tol = 1e-6): boolean =>
  Math.abs(a.x - b.x) < tol && Math.abs(a.y - b.y) < tol;

/** Os dois conjuntos de pontos são iguais, sem importar a ordem? */
export function mesmoConjunto(a: readonly Vec2[], b: readonly Vec2[]): boolean {
  if (a.length !== b.length) return false;
  const usados = new Set<number>();
  return a.every((p) => {
    const i = b.findIndex((q, k) => !usados.has(k) && mesmoPonto(p, q));
    if (i < 0) return false;
    usados.add(i);
    return true;
  });
}

/** Pontos criados pelo aluno, em coordenadas da malha (unidade = 1 quadradinho). */
export function pontosDoAluno(
  objs: ReadonlyMap<GeoId, GeoObject>,
  U: number,
  dados: ReadonlySet<string> = new Set(),
): Vec2[] {
  return [...objs.values()]
    .filter((o): o is GPoint => o.kind === 'point' && !dados.has(o.id as string))
    .map((o) => ({ x: o.pos.x / U, y: o.pos.y / U }));
}

/** Malha quadriculada de linhas, de 1 em 1, cobrindo [x0, x1] × [y0, y1]. */
export function gradeQuadriculada(
  x0: number,
  x1: number,
  y0: number,
  y1: number,
  U: number,
): Shape {
  return {
    t: 'grid',
    step: U,
    extent: { x: x0 * U, y: y0 * U, w: (x1 - x0) * U, h: (y1 - y0) * U },
    dots: false,
  };
}

/**
 * Eixos cartesianos de −n a n, numerados SÓ na origem e nos extremos.
 *
 * É a mesma regra dos gráficos da função afim: um número em cada marca polui a figura,
 * e a malha quadriculada já diz onde está cada unidade. Com −n, 0 e n escritos, o
 * aluno lê a escala e conta os quadradinhos — o que, num item de coordenadas, faz
 * parte da tarefa.
 */
export function eixosNumerados(n: number, U: number): Shape[] {
  const E = (n + 0.6) * U;
  const out: Shape[] = [
    { t: 'seg', a: { x: -E, y: 0 }, b: { x: E, y: 0 }, role: 'given', arrow: 'end' },
    { t: 'seg', a: { x: 0, y: -E }, b: { x: 0, y: E }, role: 'given', arrow: 'end' },
    { t: 'label', at: { x: E, y: 0 }, text: [txt('x')], anchor: 'se' },
    { t: 'label', at: { x: 0, y: E }, text: [txt('y')], anchor: 'nw' },
    { t: 'label', at: { x: 0, y: 0 }, text: [txt('0')], anchor: 'sw', emphasis: 'tick' },
  ];
  for (const k of [-n, n]) {
    out.push({
      t: 'label',
      at: { x: k * U, y: 0 },
      text: [txt(inteiroTxt(k))],
      anchor: 's',
      emphasis: 'tick',
    });
    out.push({
      t: 'label',
      at: { x: 0, y: k * U },
      text: [txt(inteiroTxt(k))],
      anchor: 'w',
      emphasis: 'tick',
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Quadriláteros
// ---------------------------------------------------------------------------

export type TipoQuadrilatero = 'quadrado' | 'retangulo' | 'losango' | 'paralelogramo' | 'outro';

export interface Quadrilatero {
  readonly tipo: TipoQuadrilatero;
  /** Índices dos dois pares de vértices OPOSTOS (as diagonais), quando há paralelogramo. */
  readonly diagonais: readonly [number, number, number, number] | null;
}

/**
 * Que quadrilátero quatro pontos formam?
 *
 * Um paralelogramo é o quadrilátero cujas diagonais se cortam ao meio. Entre as três
 * maneiras de separar quatro pontos em dois pares, procura-se a que tem os pontos
 * médios coincidentes: esses pares são as diagonais. Diagonais iguais fazem um
 * retângulo; perpendiculares, um losango; as duas coisas, um quadrado.
 */
export function classificarQuadrilatero(p: readonly Vec2[]): Quadrilatero {
  if (p.length !== 4) return { tipo: 'outro', diagonais: null };
  const P = p as readonly [Vec2, Vec2, Vec2, Vec2];
  const pares: readonly (readonly [number, number, number, number])[] = [
    [0, 1, 2, 3],
    [0, 2, 1, 3],
    [0, 3, 1, 2],
  ];
  for (const par of pares) {
    const [a, b, c, d] = par;
    const pa = P[a] as Vec2;
    const pb = P[b] as Vec2;
    const pc = P[c] as Vec2;
    const pd = P[d] as Vec2;
    const m1 = { x: (pa.x + pb.x) / 2, y: (pa.y + pb.y) / 2 };
    const m2 = { x: (pc.x + pd.x) / 2, y: (pc.y + pd.y) / 2 };
    if (!mesmoPonto(m1, m2)) continue;
    const u = { x: pb.x - pa.x, y: pb.y - pa.y };
    const w = { x: pd.x - pc.x, y: pd.y - pc.y };
    const l1 = u.x * u.x + u.y * u.y;
    const l2 = w.x * w.x + w.y * w.y;
    if (l1 < 1e-9 || l2 < 1e-9) continue;
    const iguais = Math.abs(l1 - l2) < 1e-6 * Math.max(l1, l2);
    const perp = Math.abs(u.x * w.x + u.y * w.y) < 1e-6 * Math.sqrt(l1 * l2);
    // Diagonais paralelas: os quatro pontos estão alinhados, não há quadrilátero.
    if (Math.abs(u.x * w.y - u.y * w.x) < 1e-9) continue;
    const tipo: TipoQuadrilatero =
      iguais && perp ? 'quadrado' : iguais ? 'retangulo' : perp ? 'losango' : 'paralelogramo';
    return { tipo, diagonais: par };
  }
  return { tipo: 'outro', diagonais: null };
}

// ---------------------------------------------------------------------------
// Revisão
// ---------------------------------------------------------------------------

/** Anéis de revisão: colorido no que está certo, vermelho no que sobra, tracejado no que falta. */
export function aneisDeRevisao(
  certos: readonly Vec2[],
  errados: readonly Vec2[],
  faltando: readonly Vec2[],
  U: number,
): Shape[] {
  const raio = 0.3 * U;
  const tela = (p: Vec2): Vec2 => ({ x: p.x * U, y: p.y * U });
  return [
    ...certos.map((p): Shape => ({ t: 'circle', c: tela(p), r: raio, role: 'result' })),
    ...errados.map((p): Shape => ({ t: 'circle', c: tela(p), r: raio, role: 'error' })),
    ...faltando.flatMap((p): Shape[] => [
      { t: 'circle', c: tela(p), r: raio, role: 'construction' },
      { t: 'point', p: tela(p), role: 'ghost' },
    ]),
  ];
}

export const LEGENDA_ANEIS: RichText = [
  txt('Anel colorido: marcação certa. Anel vermelho: marcação que não deveria estar lá. '),
  txt('Anel tracejado: ponto que faltou marcar.'),
];

/** Um passo na malha, em palavras: "2 para a direita e 3 para cima". */
export function passoTxt(dx: number, dy: number): string {
  const h = dx === 0 ? '' : `${Math.abs(dx)} ${dx > 0 ? 'para a direita' : 'para a esquerda'}`;
  const v = dy === 0 ? '' : `${Math.abs(dy)} ${dy > 0 ? 'para cima' : 'para baixo'}`;
  return h && v ? `${h} e ${v}` : h || v || 'nada';
}
